import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import type { MachineRecord } from "@nebulize/sdk";

import {
  Button,
  ConfirmDialog,
  CopyField,
  DataTable,
  EmptyState,
  Field,
  Input,
  Page,
  Panel,
  Stack,
  StatusBadge,
} from "@/components/ui";
import { broadcastAddress } from "@/lib/env";

import { Paged } from "../components/Paged";
import { Muted } from "../components/Text";

import { QueryPanel } from "./components/QueryPanel";
import {
  useApproveCanvasPairing,
  useCanvasPairings,
  useMachines,
  useRevokeMachine,
  useUnpairCanvas,
  type CanvasPairing,
} from "./lib/queries";
import { errorText, stamp } from "./lib/present";

function matchMachine(row: MachineRecord, needle: string): boolean {
  return `${row.label} ${row.short_fingerprint}`.toLowerCase().includes(needle);
}

function matchPairing(row: CanvasPairing, needle: string): boolean {
  return `${row.label} ${row.canvas_kind} ${row.id}`.toLowerCase().includes(needle);
}

/** Distinct enough for a screen reader meeting twenty of them, and never a hardcoded kind. */
function deviceName(row: CanvasPairing): string {
  return row.label === "" ? `${row.canvas_kind} ${row.id.slice(0, 8)}` : row.label;
}

/** Approve a canvas showing a code, and list what is paired. Every kind comes off the rows. */
function CanvasDevices() {
  const pairings = useCanvasPairings();
  const approve = useApproveCanvasPairing();
  const unpair = useUnpairCanvas();
  const [code, setCode] = useState("");

  return (
    <QueryPanel title="Canvas devices" rows={3} query={pairings}>
      {(rows) => (
        <Stack gap={4}>
          <form
            className="cx-listbar"
            onSubmit={(event) => {
              event.preventDefault();
              approve.mutate(code, {
                onSuccess: () => {
                  setCode("");
                },
              });
            }}
          >
            <Field label="Code from the canvas">
              {({ id }) => (
                <Input
                  id={id}
                  mono
                  value={code}
                  placeholder="ABCD-EFGH"
                  onChange={(event) => {
                    setCode(event.target.value);
                  }}
                />
              )}
            </Field>
            <Button
              type="submit"
              size="sm"
              variant="secondary"
              loading={approve.isPending}
              disabled={code.trim() === ""}
            >
              Approve
            </Button>
          </form>
          <div aria-live="polite">
            {approve.isError ? (
              <StatusBadge tone="danger">{errorText(approve.error)}</StatusBadge>
            ) : null}
            {approve.isSuccess ? <StatusBadge tone="success">paired</StatusBadge> : null}
          </div>

          {rows.length === 0 ? (
            <EmptyState bare headline="Nothing paired" />
          ) : (
            <>
              <CopyField value={broadcastAddress()} label="Paste into OBS" />
              <Paged rows={rows} pageSize={10} placeholder="Find a device" match={matchPairing}>
                {(page) => (
                  <Stack gap={3}>
                    {page.map((row) => (
                      <div className="cx-row" key={row.id}>
                        <div className="cx-row-id">
                          <div>
                            <div className="cx-node-name">{deviceName(row)}</div>
                            <Muted small>{stamp(row.last_seen_at)}</Muted>
                          </div>
                        </div>
                        <Stack direction="row" gap={3} align="center">
                          {row.revoked_at === null ? (
                            <StatusBadge tone="success">paired</StatusBadge>
                          ) : (
                            <StatusBadge tone="neutral">unpaired</StatusBadge>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Unpair ${deviceName(row)} ${row.id.slice(0, 8)}`}
                            disabled={row.revoked_at !== null || unpair.isPending}
                            onClick={() => {
                              unpair.mutate(row.id);
                            }}
                          >
                            Unpair
                          </Button>
                        </Stack>
                      </div>
                    ))}
                  </Stack>
                )}
              </Paged>
            </>
          )}
        </Stack>
      )}
    </QueryPanel>
  );
}

/** Every machine this account has paired, what it was last seen doing, and the one revoke. */
export function ConsoleMachines() {
  const navigate = useNavigate();
  const machines = useMachines();
  const revoke = useRevokeMachine();
  const [chosen, setChosen] = useState<MachineRecord | null>(null);


  return (
    <Page
      title="Machines"
      actions={
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              void navigate({ to: "/console/machines/pair" });
            }}
          >
            Pair a machine
          </Button>
      }
    >


      <QueryPanel title="Paired machines" rows={4} query={machines}>
        {(rows) =>
          rows.length === 0 ? (
            <EmptyState
              headline="No machines"
              action={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    void navigate({ to: "/console/machines/pair" });
                  }}
                >
                  Pair a machine
                </Button>
              }
            />
          ) : (
            <Paged rows={rows} pageSize={10} placeholder="Find a machine" match={matchMachine}>
              {(page) => (
                <DataTable
                  caption="Machines paired to this account"
                  density="comfortable"
                  columns={[
                    { key: "label", header: "Machine", emphasis: true },
                    { key: "short_fingerprint", header: "Fingerprint", mono: true },
                    {
                      key: "paired_at",
                      header: "Paired",
                      muted: true,
                      render: (row: MachineRecord) => stamp(row.paired_at),
                    },
                    {
                      key: "last_seen_at",
                      header: "Last seen",
                      render: (row: MachineRecord) =>
                        row.last_seen_at == null ? (
                          <StatusBadge tone="warning">never seen</StatusBadge>
                        ) : (
                          <span>{stamp(row.last_seen_at)}</span>
                        ),
                    },
                    {
                      key: "revoke",
                      header: "",
                      align: "right",
                      width: "120px",
                      render: (row: MachineRecord) => (
                        <Button
                          size="sm"
                          variant="destructive"
                          aria-label={`Revoke ${row.label}`}
                          loading={revoke.isPending && chosen?.machine_id === row.machine_id}
                          onClick={() => {
                            setChosen(row);
                          }}
                        >
                          Revoke
                        </Button>
                      ),
                    },
                  ]}
                  rows={page}
                  emptyHeadline="No matches"
                />
              )}
            </Paged>
          )
        }
      </QueryPanel>

      <CanvasDevices />

      <Panel title="Rename">
        <EmptyState bare headline="Not available" />
      </Panel>

      <ConfirmDialog
        open={chosen !== null}
        title={`Revoke ${chosen?.label ?? ""}`}
        description={`${chosen?.short_fingerprint ?? ""}: control plane closed, lease renewal refused, stored credential destroyed.`}
        confirmLabel="Revoke"
        cancelLabel="Cancel"
        loading={revoke.isPending}
        onCancel={() => {
          setChosen(null);
        }}
        onConfirm={() => {
          if (chosen === null) return;
          revoke.mutate(chosen.machine_id, {
            onSuccess: () => {
              setChosen(null);
            },
          });
        }}
      />
    </Page>
  );
}
